import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { Material, Client, MaterialAssignment } from '../types';
import { 
  Plus, 
  FileText, 
  Download, 
  Trash2, 
  Users,
  User,
  Eye,
  Edit,
  X,
  Check
} from 'lucide-react';

interface MaterialUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: () => void;
}

const MaterialUploadModal: React.FC<MaterialUploadModalProps> = ({ isOpen, onClose, onUpload }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [assignedTo, setAssignedTo] = useState<'all' | 'specific' | 'category'>('all');
  const [selectedClients, setSelectedClients] = useState<number[]>([]);
  const [assignedCategory, setAssignedCategory] = useState<'1-1' | 'group' | ''>('');
  const [availableClients, setAvailableClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadAvailableClients();
    }
  }, [isOpen]);

  const loadAvailableClients = async () => {
    try {
      const clients = await apiService.getAvailableClients();
      setAvailableClients(clients);
    } catch (error) {
      console.error('Failed to load clients:', error);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Validate file type
      const allowedTypes = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt'];
      const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();
      
      if (!fileExtension || !allowedTypes.includes(fileExtension)) {
        setError('Please select a valid file type (PDF, Word, Excel, or Text)');
        return;
      }

      // Validate file size (10MB limit)
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }

      setFile(selectedFile);
      setError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !file) {
      setError('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);
      formData.append('file', file);
      formData.append('assigned_to', assignedTo);
      
      if (assignedTo === 'specific' && selectedClients.length > 0) {
        selectedClients.forEach(clientId => {
          formData.append('assigned_clients', clientId.toString());
        });
      }
      
      if (assignedTo === 'category' && assignedCategory) {
        formData.append('assigned_category', assignedCategory);
      }

      await apiService.uploadMaterial(formData);
      
      // Reset form
      setTitle('');
      setDescription('');
      setFile(null);
      setAssignedTo('all');
      setSelectedClients([]);
      setAssignedCategory('');
      
      onUpload();
      onClose();
    } catch (error: any) {
      setError(error.message || 'Failed to upload material');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleClientSelection = (clientId: number) => {
    setSelectedClients(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Upload New Material</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter material title"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter material description"
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              File *
            </label>
            <input
              type="file"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            <p className="text-sm text-gray-500 mt-1">
              Supported formats: PDF, Word, Excel, Text (Max 10MB)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assignment
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="all"
                  checked={assignedTo === 'all'}
                  onChange={(e) => {
                    setAssignedTo(e.target.value as 'all' | 'specific' | 'category');
                    setAssignedCategory('');
                  }}
                  className="mr-2"
                />
                <Users className="h-4 w-4 mr-2" />
                Assign to all clients
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="category"
                  checked={assignedTo === 'category'}
                  onChange={(e) => {
                    setAssignedTo(e.target.value as 'all' | 'specific' | 'category');
                    setSelectedClients([]);
                  }}
                  className="mr-2"
                />
                <Users className="h-4 w-4 mr-2" />
                Assign by category
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="specific"
                  checked={assignedTo === 'specific'}
                  onChange={(e) => {
                    setAssignedTo(e.target.value as 'all' | 'specific' | 'category');
                    setAssignedCategory('');
                  }}
                  className="mr-2"
                />
                <User className="h-4 w-4 mr-2" />
                Assign to specific clients
              </label>
            </div>
          </div>

          {assignedTo === 'category' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Category
              </label>
              <select
                value={assignedCategory}
                onChange={(e) => setAssignedCategory(e.target.value as '1-1' | 'group')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select a category</option>
                <option value="1-1">1-on-1 Clients</option>
                <option value="group">Group Clients</option>
              </select>
            </div>
          )}

          {assignedTo === 'specific' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Clients
              </label>
              <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2">
                {availableClients.map(client => (
                  <label key={client.id} className="flex items-center py-1">
                    <input
                      type="checkbox"
                      checked={selectedClients.includes(client.id)}
                      onChange={() => toggleClientSelection(client.id)}
                      className="mr-2"
                    />
                    {client.user.first_name} {client.user.last_name} ({client.user.email})
                  </label>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="text-red-600 text-sm">{error}</div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 'Uploading...' : 'Upload Material'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface MaterialAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  material: Material | null;
  onAssign: () => void;
}

const MaterialAssignmentModal: React.FC<MaterialAssignmentModalProps> = ({ 
  isOpen, 
  onClose, 
  material, 
  onAssign 
}) => {
  const [assignedTo, setAssignedTo] = useState<'all' | 'specific' | 'category'>('all');
  const [selectedClients, setSelectedClients] = useState<number[]>([]);
  const [assignedCategory, setAssignedCategory] = useState<'1-1' | 'group' | ''>('');
  const [availableClients, setAvailableClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && material) {
      loadAvailableClients();
      setAssignedTo(material.assigned_to);
      setSelectedClients(material.assigned_clients || []);
      setAssignedCategory(material.assigned_category || '');
    }
  }, [isOpen, material]);

  const loadAvailableClients = async () => {
    try {
      const clients = await apiService.getAvailableClients();
      setAvailableClients(clients);
    } catch (error) {
      console.error('Failed to load clients:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!material) return;

    setIsLoading(true);
    setError('');

    try {
      const assignmentData = {
        material_id: material.id,
        client_ids: assignedTo === 'specific' ? selectedClients : [],
        assign_to_all: assignedTo === 'all'
      };

      await apiService.assignMaterial(material.id, assignmentData);
      onAssign();
      onClose();
    } catch (error: any) {
      setError(error.message || 'Failed to assign material');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleClientSelection = (clientId: number) => {
    setSelectedClients(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  if (!isOpen || !material) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Assign Material</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="mb-4">
          <h3 className="font-medium text-gray-900">{material.title}</h3>
          <p className="text-sm text-gray-500">{material.description}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assignment
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="all"
                  checked={assignedTo === 'all'}
                  onChange={(e) => {
                    setAssignedTo(e.target.value as 'all' | 'specific' | 'category');
                    setAssignedCategory('');
                  }}
                  className="mr-2"
                />
                <Users className="h-4 w-4 mr-2" />
                Assign to all clients
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="category"
                  checked={assignedTo === 'category'}
                  onChange={(e) => {
                    setAssignedTo(e.target.value as 'all' | 'specific' | 'category');
                    setSelectedClients([]);
                  }}
                  className="mr-2"
                />
                <Users className="h-4 w-4 mr-2" />
                Assign by category
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="specific"
                  checked={assignedTo === 'specific'}
                  onChange={(e) => {
                    setAssignedTo(e.target.value as 'all' | 'specific' | 'category');
                    setAssignedCategory('');
                  }}
                  className="mr-2"
                />
                <User className="h-4 w-4 mr-2" />
                Assign to specific clients
              </label>
            </div>
          </div>

          {assignedTo === 'category' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Category
              </label>
              <select
                value={assignedCategory}
                onChange={(e) => setAssignedCategory(e.target.value as '1-1' | 'group')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select a category</option>
                <option value="1-1">1-on-1 Clients</option>
                <option value="group">Group Clients</option>
              </select>
            </div>
          )}

          {assignedTo === 'specific' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Clients
              </label>
              <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2">
                {availableClients.map(client => (
                  <label key={client.id} className="flex items-center py-1">
                    <input
                      type="checkbox"
                      checked={selectedClients.includes(client.id)}
                      onChange={() => toggleClientSelection(client.id)}
                      className="mr-2"
                    />
                    {client.user.first_name} {client.user.last_name}
                  </label>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="text-red-600 text-sm">{error}</div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 'Assigning...' : 'Assign Material'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Materials: React.FC = () => {
  const { user } = useAuth();
  const [materials, setMaterials] = useState<Material[]>([]);
  const [assignments, setAssignments] = useState<MaterialAssignment[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [clientProfile, setClientProfile] = useState<Client | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(null);
  const [viewMode, setViewMode] = useState<'materials' | 'assignments'>('materials');

  const isTrainer = user?.role === 'trainer';
  const isGroupClient = clientProfile?.category === 'group';

  useEffect(() => {
    if (!isTrainer && user?.role === 'client') {
      // Fetch client profile to check category
      apiService.getCurrentClientProfile()
        .then(profile => {
          setClientProfile(profile);
          // Reload data after profile is fetched to apply filtering
          loadData();
        })
        .catch(err => {
          console.error('Error fetching client profile:', err);
          loadData();
        });
    } else {
      loadData();
    }
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError('');

    try {
      if (isTrainer) {
        const [materialsData, assignmentsData, statsData] = await Promise.all([
          apiService.getMaterials(),
          apiService.getMaterialAssignments(),
          apiService.getMaterialStats()
        ]);
        console.log('Loaded materialsData:', materialsData);
        if (materialsData && (Array.isArray(materialsData) || materialsData.results)) {
          const loadedMaterials = Array.isArray(materialsData) ? materialsData : materialsData.results;
          loadedMaterials.forEach((mat, idx) => {
            console.log(`Material #${idx + 1}:`, mat);
            console.log(`Material #${idx + 1} file_url:`, mat.file_url);
          });
          setMaterials(loadedMaterials);
        } else {
          setMaterials([]);
        }
        setAssignments(assignmentsData.results);
        setStats(statsData);
      } else {
        const materialsData = await apiService.getMaterials(1, true);
        console.log('Loaded materialsData (client):', materialsData);
        if (materialsData && (Array.isArray(materialsData) || materialsData.results)) {
          let loadedMaterials = Array.isArray(materialsData) ? materialsData : materialsData.results;
          
          // Filter materials for group clients - only show materials assigned to 'group' category or 'all'
          if (clientProfile?.category === 'group') {
            loadedMaterials = loadedMaterials.filter((mat: Material) => 
              mat.assigned_to === 'all' || 
              (mat.assigned_to === 'category' && mat.assigned_category === 'group') ||
              (mat.assigned_to === 'specific' && mat.assigned_clients?.includes(clientProfile.id))
            );
          }
          
          loadedMaterials.forEach((mat, idx) => {
            console.log(`Material #${idx + 1} (client):`, mat);
            console.log(`Material #${idx + 1} file_url (client):`, mat.file_url);
          });
          setMaterials(loadedMaterials);
        } else {
          setMaterials([]);
        }
      }
    } catch (error: any) {
      setError(error.message || 'Failed to load materials');
      setMaterials([]);
      setAssignments([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (material: Material) => {
    try {
      const result = await apiService.downloadMaterial(material.id);
      
      // Check if this is an S3 file with direct URL
      if (typeof result === 'object' && 'download_url' in result) {
        // S3 file - open in new tab
        window.open(result.download_url, '_blank');
      } else {
        // Local file - download as blob
        const blob = result as Blob;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = material.title;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error: any) {
      setError(error.message || 'Failed to download material');
    }
  };

  const handleDelete = async (material: Material) => {
    if (!window.confirm(`Are you sure you want to delete "${material.title}"?`)) {
      return;
    }

    try {
      await apiService.deleteMaterial(material.id);
      loadData();
    } catch (error: any) {
      setError(error.message || 'Failed to delete material');
    }
  };

  const handleAssign = (material: Material) => {
    setSelectedMaterial(material);
    setShowAssignmentModal(true);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileType: string) => {
    switch (fileType) {
      case 'pdf':
        return '📄';
      case 'doc':
      case 'docx':
        return '📝';
      case 'xls':
      case 'xlsx':
        return '📊';
      case 'txt':
        return '📄';
      default:
        return '📄';
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isGroupClient ? 'Group Guides' : 'Materials'}
            </h1>
            <p className="mt-2 text-gray-600">
              {isTrainer 
                ? 'Upload and manage training materials' 
                : isGroupClient 
                  ? 'Access guides and resources assigned to your group'
                  : 'Access your assigned training materials'}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {isTrainer && (
              <button
                onClick={() => setShowUploadModal(true)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <Plus className="h-5 w-5 mr-2" />
                Upload Material
              </button>
            )}
          </div>
        </div>

        {isTrainer && stats && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-blue-600">{stats.total_materials}</div>
              <div className="text-sm text-gray-600">Total Materials</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-green-600">{stats.assigned_to_all}</div>
              <div className="text-sm text-gray-600">Assigned to All</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-yellow-600">{stats.assigned_to_specific}</div>
              <div className="text-sm text-gray-600">Assigned to Specific</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-red-600">{stats.unassigned}</div>
              <div className="text-sm text-gray-600">Unassigned</div>
            </div>
          </div>
        )}
      </div>

      {isTrainer && (
        <div className="mb-6">
          <div className="flex space-x-4">
            <button
              onClick={() => setViewMode('materials')}
              className={`px-4 py-2 rounded-md ${
                viewMode === 'materials'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Materials
            </button>
            <button
              onClick={() => setViewMode('assignments')}
              className={`px-4 py-2 rounded-md ${
                viewMode === 'assignments'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Assignments
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {viewMode === 'materials' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.isArray(materials) && materials.map((material) => (
            <div key={material.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">{getFileIcon(material.file_type)}</span>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {material.title}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {material.file_type.toUpperCase()} • {formatFileSize(material.file_size)}
                      </p>
                    </div>
                  </div>
                  {isTrainer && (
                    <div className="flex space-x-1">
                      <button
                        onClick={() => handleAssign(material)}
                        className="p-1 text-gray-400 hover:text-blue-600"
                        title="Assign"
                      >
                        <Users className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(material)}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>

                {material.description && (
                  <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                    {material.description}
                  </p>
                )}

                <div className="mt-4 flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    {material.assigned_to === 'all' ? (
                      <span className="flex items-center">
                        <Users className="h-3 w-3 mr-1" />
                        All clients
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <User className="h-3 w-3 mr-1" />
                        {material.assigned_clients?.length || 0} clients
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => handleDownload(material)}
                    className="inline-flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </button>
                </div>

                <div className="mt-2 text-xs text-gray-400">
                  Uploaded {new Date(material.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {viewMode === 'assignments' && isTrainer && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Material Assignments</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {Array.isArray(assignments) && assignments.map((assignment) => (
              <div key={assignment.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">
                      {assignment.material.title}
                    </h4>
                    <p className="text-sm text-gray-500">
                      Assigned to {assignment.client.user.first_name} {assignment.client.user.last_name}
                    </p>
                  </div>
                  <div className="text-sm text-gray-500">
                    {new Date(assignment.assigned_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
            {Array.isArray(assignments) && assignments.length === 0 && (
              <div className="px-6 py-8 text-center text-gray-500">
                No assignments found
              </div>
            )}
          </div>
        </div>
      )}

      {Array.isArray(materials) && materials.length === 0 && viewMode === 'materials' && (
        <div className="text-center py-12">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No materials</h3>
          <p className="mt-1 text-sm text-gray-500">
            {isTrainer 
              ? 'Get started by uploading your first training material.'
              : 'No materials have been assigned to you yet.'
            }
          </p>
          {isTrainer && (
            <div className="mt-6">
              <button
                onClick={() => setShowUploadModal(true)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <Plus className="h-5 w-5 mr-2" />
                Upload Material
              </button>
            </div>
          )}
        </div>
      )}

      <MaterialUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={loadData}
      />

      <MaterialAssignmentModal
        isOpen={showAssignmentModal}
        onClose={() => setShowAssignmentModal(false)}
        material={selectedMaterial}
        onAssign={loadData}
      />
    </div>
  );
};

export default Materials; 